import { Repeat } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/design-system/lib/cn';
import { DateField } from '@/design-system/ui/DateField';
import { SegmentedControl } from '@/design-system/ui/SegmentedControl';
import { SimpleSelect } from '@/design-system/ui/Select';
import { Stepper } from '@/design-system/ui/Stepper';
import { WEEKDAY_LONG, addDays } from '@escala/domain/dates';
import {
  MAX_INTERVAL,
  MAX_SERIES_OCCURRENCES,
  type RecurrenceEnd,
  type RecurrenceFreq,
  type RecurrenceRule,
  neverEndingUntil,
  presetOf,
  recurrenceError,
  recurrenceLabel,
  recurrencePresets,
  seriesSummary,
} from '@escala/domain/recurrence';

const NONE = 'none';
const CUSTOM = 'custom';

/** O "D S T Q Q S S" da Agenda do Google, com o nome inteiro para o leitor de tela. */
const WEEKDAY_INITIAL = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;

const UNIT: Record<RecurrenceFreq, [string, string]> = {
  daily: ['dia', 'dias'],
  weekly: ['semana', 'semanas'],
  monthly: ['mês', 'meses'],
  yearly: ['ano', 'anos'],
};

type EndKind = RecurrenceEnd['kind'];

/**
 * A repetição do evento novo — o melhor da Agenda do Google e do Calendário do iOS: as
 * opções rápidas saem da data ("Semanal: cada domingo", "Mensal: no terceiro domingo",
 * "Quinzenal…"), "Personalizar…" abre a cada N dias/semanas/meses/anos, os dias da semana
 * e o mensal no dia ou no n-ésimo dia da semana, e o fim é escolhido à parte (nunca, numa
 * data, após N vezes), com o resumo das datas que serão criadas.
 */
export default function RecurrenceField({
  date,
  value,
  onChange,
}: {
  /** A data do evento: a primeira da série e a base das opções rápidas. */
  date: string;
  value: RecurrenceRule | null;
  onChange: (rule: RecurrenceRule | null) => void;
}) {
  const presets = recurrencePresets(date, value?.end);
  const rule = value ? { ...value, start: date } : null;
  const preset = rule ? presetOf(rule, date) : null;
  // Aberta pelo "Personalizar…", a personalizada fica aberta mesmo que bata com uma opção rápida.
  const [customOpen, setCustomOpen] = useState(() => !!rule && !preset);
  const custom = !!rule && (customOpen || !preset);
  let selected: string = NONE;

  if (rule) selected = custom ? CUSTOM : preset!;

  const error = rule ? recurrenceError(rule) : null;

  const choose = (key: string) => {
    setCustomOpen(key === CUSTOM);

    if (key === NONE) return onChange(null);
    // "Personalizar…" começa da regra escolhida (ou da semanal na data).
    if (key === CUSTOM) return onChange(rule ?? presets[0]!.rule);

    onChange(presets.find((p) => p.key === key)!.rule);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="event-repeat" className="flex items-center gap-1.5 text-small font-semibold">
          <Repeat aria-hidden className="size-4 stroke-[1.8] text-ink-subtle" />
          Repetição
        </label>
        <SimpleSelect
          id="event-repeat"
          value={selected}
          onValueChange={choose}
          searchable={false}
          options={[
            { value: NONE, label: 'Não se repete', pin: true },
            ...presets.map((p) => ({ value: p.key, label: p.label, pin: true })),
            {
              value: CUSTOM,
              label: custom && rule ? `Personalizada: ${recurrenceLabel(rule)}` : 'Personalizar…',
              pin: true,
            },
          ]}
        />
      </div>

      {rule && custom ? <CustomRule date={date} rule={rule} onChange={onChange} /> : null}
      {rule ? <EndField date={date} rule={rule} onChange={onChange} /> : null}

      {rule ? (
        <p
          role={error ? 'alert' : 'status'}
          className={cn('m-0 text-caption', error ? 'text-status-conflict' : 'text-ink-subtle')}
        >
          {error ?? seriesSummary(rule)}
        </p>
      ) : null}
    </div>
  );
}

/** "Personalizar…": a cada N, os dias da semana, o mensal no dia ou no n-ésimo dia. */
function CustomRule({
  date,
  rule,
  onChange,
}: {
  date: string;
  rule: RecurrenceRule;
  onChange: (rule: RecurrenceRule) => void;
}) {
  const presets = recurrencePresets(date, rule.end);
  const of = (key: string) => presets.find((p) => p.key === key)?.rule;
  const monthlyOptions = presets.filter((p) => p.rule.freq === 'monthly');
  const monthlyKey = monthlyOptions.find(
    (p) => JSON.stringify(p.rule.monthly) === JSON.stringify(rule.monthly)
  )?.key;
  const [one, many] = UNIT[rule.freq];

  const setFreq = (freq: RecurrenceFreq) => {
    const base: Record<RecurrenceFreq, string> = {
      daily: 'daily',
      weekly: 'weekly',
      monthly: 'monthlyDay',
      yearly: 'yearly',
    };

    onChange({ ...of(base[freq])!, interval: rule.interval });
  };

  const toggleDay = (day: number) => {
    const weekdays = rule.weekdays.includes(day)
      ? rule.weekdays.filter((d) => d !== day)
      : [...rule.weekdays, day].sort((a, b) => a - b);

    onChange({ ...rule, weekdays });
  };

  return (
    <fieldset className="m-0 flex flex-col gap-3 rounded-block border border-line-input p-3">
      <legend className="px-1 text-small font-semibold">Repetição personalizada</legend>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-small">Repetir a cada</span>
        <Stepper
          aria-label="Repetir a cada"
          min={1}
          max={MAX_INTERVAL}
          value={rule.interval}
          onValueChange={(interval) => onChange({ ...rule, interval })}
        />
        <SimpleSelect
          aria-label="Unidade da repetição"
          size="sm"
          className="w-32"
          searchable={false}
          value={rule.freq}
          onValueChange={(v) => setFreq(v as RecurrenceFreq)}
          options={(Object.keys(UNIT) as RecurrenceFreq[]).map((f) => ({
            value: f,
            label: rule.interval === 1 ? UNIT[f][0] : UNIT[f][1],
            pin: true,
          }))}
        />
      </div>

      {rule.freq === 'weekly' ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-small">Nos dias</span>
          <div role="group" aria-label="Dias da semana" className="flex flex-wrap gap-1.5">
            {WEEKDAY_INITIAL.map((initial, day) => {
              const on = rule.weekdays.includes(day);

              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={on}
                  aria-label={WEEKDAY_LONG[day]}
                  title={WEEKDAY_LONG[day]}
                  onClick={() => toggleDay(day)}
                  className={cn(
                    'grid size-9 place-items-center rounded-pill border text-small font-semibold',
                    on
                      ? 'border-primary bg-primary text-white'
                      : 'border-line-input bg-white text-ink-secondary hover:bg-surface-subtle'
                  )}
                >
                  {initial}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {rule.freq === 'monthly' ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-small">No mês</span>
          <SegmentedControl
            aria-label="Dia do mês"
            block
            value={monthlyKey ?? 'monthlyDay'}
            onValueChange={(key) => onChange({ ...of(key)!, interval: rule.interval })}
            options={monthlyOptions.map((p) => ({ value: p.key, label: p.label.replace('Mensal: ', '') }))}
          />
        </div>
      ) : null}

      {rule.freq === 'yearly' ? (
        <span className="text-small text-ink-muted">
          {presets.find((p) => p.key === 'yearly')!.label.replace('Anual: em', 'Todo ano em')}
        </span>
      ) : null}

      <span className="text-caption text-ink-subtle">
        A cada {rule.interval} {rule.interval === 1 ? one : many}: {recurrenceLabel(rule)}.
      </span>
    </fieldset>
  );
}

/** "Termina": nunca, numa data ou depois de N vezes (o "Terminar Repetição" do iOS). */
function EndField({
  date,
  rule,
  onChange,
}: {
  date: string;
  rule: RecurrenceRule;
  onChange: (rule: RecurrenceRule) => void;
}) {
  const kind: EndKind = rule.end.kind;

  const setKind = (next: EndKind) => {
    if (next === 'never') onChange({ ...rule, end: { kind: 'never' } });
    // A data de fim começa três meses depois; as vezes, em 10.
    if (next === 'until') onChange({ ...rule, end: { kind: 'until', date: addDays(date, 91) } });
    if (next === 'count') onChange({ ...rule, end: { kind: 'count', count: 10 } });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-small font-semibold">Termina</span>
      <SegmentedControl
        aria-label="Quando a repetição termina"
        block
        value={kind}
        onValueChange={setKind}
        options={[
          { value: 'never', label: 'Nunca' },
          { value: 'until', label: 'Em uma data' },
          { value: 'count', label: 'Após' },
        ]}
      />
      {rule.end.kind === 'never' ? (
        <span className="text-caption text-ink-subtle">
          Sem data de fim: criamos todas as datas até o fim de {neverEndingUntil(date).slice(0, 4)}.
        </span>
      ) : null}
      {rule.end.kind === 'until' ? (
        <label className="flex flex-col gap-1.5">
          <span className="text-small">Data do término</span>
          <DateField
            min={date}
            value={rule.end.date}
            onChange={(iso) => onChange({ ...rule, end: { kind: 'until', date: iso || date } })}
          />
        </label>
      ) : null}
      {rule.end.kind === 'count' ? (
        <div className="flex items-center gap-2">
          <Stepper
            aria-label="Quantas vezes"
            min={1}
            max={MAX_SERIES_OCCURRENCES}
            value={rule.end.count}
            onValueChange={(count) => onChange({ ...rule, end: { kind: 'count', count } })}
          />
          <span className="text-small">{rule.end.count === 1 ? 'vez' : 'vezes'}</span>
        </div>
      ) : null}
    </div>
  );
}
